import { type FC, useEffect, useMemo, memo, useCallback } from 'react';
import * as THREE from 'three';
import { type ImageProps, type ImageViewerProps, type PointPickResult } from '../../types';
import { ViewerLayer } from '../ViewerLayer';
import { cameraEulerFromAbyssRotation } from '../Util';
import { RENDER_ORDER_IMAGE_VIEWER } from '../RenderOrder';
import { ImageType } from '../Enums';
import { NeighbouringImageSet } from './NeighbouringImageSet';
import { useNeighbouringImages } from './useNeighbouringImages';
import { type NeighbouringImageProps } from './NeighbouringImage';
import { useImageMaterial } from '../Shaders/useImageMaterial';
import { useTextureLoader } from '../Loaders';

export interface ImageViewerExtendedProps extends ImageViewerProps {
  pickPointCloud: (nativeEvent: PointerEvent | MouseEvent) => PointPickResult | undefined;
  allImages?: ImageProps[];
  showMaxNeighbouringImages?: number;
  maxNeighbouringImageAngle?: number;
  brightness: number;
  contrast: number;
}

export const ImageViewer: FC<ImageViewerExtendedProps> = memo(
  ({
    image,
    opacity,
    brightness,
    contrast,
    // onClick,
    // pickPointCloud,
    setNextCurrentImage,
    allImages,
    showMaxNeighbouringImages = 0, // this disables built-in neighbouring images by default
    maxNeighbouringImageAngle,
  }) => {
    const {
      url,
      requestHeader,
      position: positionProps,
      rotation: rotationProps,
      distance = 50,
      width,
      height,
      focalLength,
      cx,
      cy,
    } = image;
    const { texture } = useTextureLoader({ url, requestHeader });

    const updatedTexture = useMemo(() => {
      if (texture) {
        // more crispy image with LinearFilter and without mipmaps
        texture.generateMipmaps = false;
        texture.minFilter = THREE.LinearFilter;
        // texture.wrapS = THREE.RepeatWrapping;
        // texture.repeat.x = -1;
        texture.needsUpdate = true;
      }
      return texture;
    }, [texture]);

    const imageMaterial = useImageMaterial({
      map: updatedTexture,
      side: THREE.FrontSide,
      transparent: true,
      toneMapped: false,
      opacity,
      brightness,
      contrast,
    });

    const pixelsToMeters = useMemo(() => {
      return distance / focalLength;
    }, [distance, focalLength]);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        // Esc key
        const { altKey, ctrlKey, metaKey, shiftKey } = event;
        if (event.keyCode === 27 && !altKey && !ctrlKey && !metaKey && !shiftKey) {
          setNextCurrentImage(undefined);
          event.stopPropagation();
        }
      };
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [setNextCurrentImage]);

    const handleMesh = useCallback((mesh: THREE.Object3D | null) => {
      mesh?.layers.enable(ViewerLayer.AutoPickable);
    }, []);

    const rotation = useMemo(() => {
      return cameraEulerFromAbyssRotation(rotationProps);
    }, [rotationProps]);

    const position = useMemo(() => {
      const cameraPosition = new THREE.Vector3(...positionProps);
      const derectionVec = new THREE.Vector3(0, 0, -distance);
      derectionVec.applyEuler(rotation);
      return cameraPosition.add(derectionVec);
    }, [positionProps, rotation, distance]);

    const layers = useMemo(() => {
      const layers0 = new THREE.Layers();
      if (opacity > 0.0001) {
        layers0.set(ViewerLayer.SphericalViewerLayer);
      }
      return layers0;
    }, [opacity]);

    const { neighbouringImages } =
      useNeighbouringImages({
        image,
        allImages,
        showMaxNeighbouringImages,
        maxNeighbouringImageAngle,
        onClick: (image: NeighbouringImageProps) =>
          setNextCurrentImage({ imageType: ImageType.Rectilinear, ...image.image }),
        onHover: () => {},
      }) ?? {};

    return (
      <>
        <group
          position={position}
          rotation={rotation}
          scale={[pixelsToMeters, pixelsToMeters, pixelsToMeters]}
        >
          <mesh
            ref={handleMesh}
            layers={layers}
            // rotation={[Math.PI / 2, Math.PI / 2, 0]}
            // position={[cx * pixelsToMeters, cy * pixelsToMeters, 0]}
            position={[-cx, cy, 0]}
            renderOrder={RENDER_ORDER_IMAGE_VIEWER}
            material={imageMaterial}
            // onClick={clickHandler}
          >
            <planeGeometry args={[width, height]} />
          </mesh>
        </group>
        {neighbouringImages && <NeighbouringImageSet images={neighbouringImages} />}
      </>
    );
  }
);
ImageViewer.displayName = 'ImageViewer';
